/**
 * Crew Dashboard Query Hook
 *
 * Combines multiple queries for the crew dashboard view with polling.
 */

import { useQueries, useQueryClient } from '@tanstack/react-query';
import {
  getPendingEvents,
  getMyAvailability,
  type PendingEvent,
  type Availability,
} from '../../api/availability';
import { getMyAssignments, type Assignment } from '../../api/assignments';
import { queryKeys, POLL_INTERVALS } from '../../lib/queryClient';
import { getErrorMessage } from '../../api/client';
import { useOrganization } from '../../context/OrganizationContext';

interface UseCrewDashboardOptions {
  /** Enable/disable automatic polling (default: true) */
  polling?: boolean;
  /** Custom polling interval in ms (default: 30000) */
  pollingInterval?: number;
}

export function useCrewDashboard(options: UseCrewDashboardOptions = {}) {
  const { polling = true, pollingInterval = POLL_INTERVALS.DASHBOARD } = options;
  const { currentOrg, isLoading: orgLoading } = useOrganization();

  // Only enable queries when org context is ready
  const queryEnabled = !orgLoading && !!currentOrg;

  const results = useQueries({
    queries: [
      {
        queryKey: [...queryKeys.availability.pending(), currentOrg?.orgSlug],
        queryFn: getPendingEvents,
        enabled: queryEnabled,
        refetchInterval: polling ? pollingInterval : false,
        refetchIntervalInBackground: false,
        staleTime: pollingInterval / 2,
      },
      {
        queryKey: [...queryKeys.availability.my(), currentOrg?.orgSlug],
        queryFn: getMyAvailability,
        enabled: queryEnabled,
        refetchInterval: polling ? pollingInterval : false,
        refetchIntervalInBackground: false,
        staleTime: pollingInterval / 2,
      },
      {
        queryKey: [...queryKeys.assignments.my(), currentOrg?.orgSlug],
        queryFn: () => getMyAssignments(),
        enabled: queryEnabled,
        refetchInterval: polling ? pollingInterval : false,
        refetchIntervalInBackground: false,
        staleTime: pollingInterval / 2,
      },
    ],
  });

  const [pendingEventsQuery, myAvailabilityQuery, myAssignmentsQuery] = results;

  // Combined loading state - include org loading
  const loading = orgLoading || results.some((r) => r.isLoading);
  const isRefetching = results.some((r) => r.isRefetching);

  // First error found
  const errorResult = results.find((r) => r.error);
  const error = errorResult?.error ? getErrorMessage(errorResult.error) : null;

  // Most recent update time
  const dataUpdatedAt = Math.max(...results.map((r) => r.dataUpdatedAt ?? 0));

  return {
    // Data
    pendingEvents: (pendingEventsQuery.data?.events ?? []) as PendingEvent[],
    myAvailability: (myAvailabilityQuery.data?.availability ?? []) as Availability[],
    myAssignments: (myAssignmentsQuery.data?.assignments ?? []) as Assignment[],

    // States
    loading,
    error,
    isRefetching,

    // Timestamps for UI
    dataUpdatedAt,

    // Actions
    refetch: () => {
      results.forEach((r) => r.refetch());
    },
  };
}

/**
 * Hook for invalidating crew dashboard data after mutations
 */
export function useInvalidateCrewDashboard() {
  const queryClient = useQueryClient();

  return () => {
    queryClient.invalidateQueries({ queryKey: queryKeys.availability.pending() });
    queryClient.invalidateQueries({ queryKey: queryKeys.availability.my() });
    queryClient.invalidateQueries({ queryKey: queryKeys.assignments.my() });
  };
}
